import { sendRecoverPassword } from 'user/pages/RecoverPassword/services/recoverPassword';
import { Col, Container, Row } from 'react-bootstrap';
import { useState, useEffect } from 'react';
import { useRootLoadingSpinner } from 'shared/spinner';
import { Button } from '@orbita-ui/core';
import { useNavigate } from 'react-router-dom';
import { LOGIN_PATH } from 'user/routes';
import { FormWithValidation } from 'shared/form';
import { LOGIN_ERROR_MESSAGES } from 'auth/constants/LoginErrorMessages';
import { RecoverPasswordStep } from './models/RecoverPasswordStep';
import { RecoverPasswordFormData } from './models/RecoverPasswordFormData';
import { RecoverPasswordData } from './models/RecoverPasswordData';
import { Card } from 'shared/components';
import { useAuth } from 'auth/hooks/useAuth';
import { RecoverPasswordResponseError } from './models/RecoverPasswordResponseError';

export function RecoverPasswordPage() {
  const navigate = useNavigate();
  const { isLogged, logout } = useAuth();

  const { setRootLoadingSpinnerStatus } = useRootLoadingSpinner();

  const [userInfo, setUserInfo] = useState<RecoverPasswordData>({
    username: '',
    mail: '',
  });
  const [step, setStep] = useState(RecoverPasswordStep.USER_FORM);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setRootLoadingSpinnerStatus(true);

    if (isLogged()) {
      logout();
    }

    setRootLoadingSpinnerStatus(false);
  }, [setRootLoadingSpinnerStatus, isLogged, logout]);

  const goLoginHandler = () => {
    navigate(LOGIN_PATH);
  };

  const searchUserHandler = (data: RecoverPasswordFormData) => {
    setUserInfo({ ...userInfo, ...data });
    setRootLoadingSpinnerStatus(true);
    setIsLoading(true);

    sendRecoverPassword(data)
      .then(() => {
        setStep(RecoverPasswordStep.EMAIL_SENT);
      })
      .catch((error: RecoverPasswordResponseError) => {
        if (error.isServerError) {
          setStep(RecoverPasswordStep.ERROR);
        } else {
          // Si no es 5xx, igual mostramos mensaje genérico
          setStep(RecoverPasswordStep.EMAIL_SENT);
        }
      })
      .finally(() => setRootLoadingSpinnerStatus(false));
  };

  const changeUserHandler = () => {
    setStep(RecoverPasswordStep.USER_FORM);
  };

  const getCardContent = (step: string) => {
    switch (step) {
      case RecoverPasswordStep.USER_FORM:
        return (
          <Card className="mt-4 p-4">
            <p>Para restablecer tu clave de acceso, ingresá tu nombre de usuario</p>
            <br />
            <FormWithValidation onSubmit={searchUserHandler}>
              <FormWithValidation.TextInput
                name="username"
                placeholder="Nombre de usuario"
                fullWidth
                registerOptions={{
                  required: LOGIN_ERROR_MESSAGES.usernameRequired,
                }}
              />
              <Col className="text-end">
                <Button color="inverted" className="me-2" onClick={goLoginHandler}>
                  Volver
                </Button>
                <Button type="submit" disabled={isLoading}>
                  Continuar
                </Button>
              </Col>
            </FormWithValidation>
          </Card>
        );

      case RecoverPasswordStep.EMAIL_SENT:
        return (
          <Card variant="info" className="mt-4 p-4">
            <p>
              Si la información proporcionada corresponde a una cuenta existente, se
              enviará un correo electrónico con las instrucciones para restablecer la
              contraseña. Por favor, revise su bandeja de entrada y la carpeta de spam.
            </p>
            <br />
            <Col className="text-end">
              <Button type="button" onClick={goLoginHandler}>
                Volver al login
              </Button>
            </Col>
          </Card>
        );

      case RecoverPasswordStep.ERROR:
        return (
          <Card variant="error" className="mt-4 p-4">
            <p>
              Ocurrió un error inesperado al procesar la solicitud. Por favor, intentá
              nuevamente más tarde.
            </p>
            <br />
            <Col className="text-end">
              <Button type="button" onClick={changeUserHandler}>
                Volver
              </Button>
            </Col>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <Container>
      <Row>
        <Col>{getCardContent(step)}</Col>
      </Row>
    </Container>
  );
}
